import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUITheme, AXNUIThemeConfirmButton } from "@iddataweb/idw-common-ui/types";
import { ThemingActionButtonValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingActionButtonStyleSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiTheme: AXNUITheme;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingActionButtonValidationErrors;
    updateAXNUIThemeConfirmButtonFieldValue: (key: keyof AXNUIThemeConfirmButton, value: string | boolean) => void;
};

export const ThemingActionButtonStyleSection = (props: ThemingActionButtonStyleSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiTheme,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        updateAXNUIThemeConfirmButtonFieldValue
    } = props;

    const handleActionButtonBackgroundColorChange = (newValue: string) => {
        updateAXNUIThemeConfirmButtonFieldValue("backgroundColor", newValue);
    };

    const handleResetActionButtonBackgroundColor = () => {
        updateAXNUIThemeConfirmButtonFieldValue("backgroundColor", inheritedAXNUITheme.confirmButton.backgroundColor);
    };

    const handleActionButtonTextColorChange = (newValue: string) => {
        updateAXNUIThemeConfirmButtonFieldValue("textColor", newValue);
    };

    const handleResetActionButtonTextColor = () => {
        updateAXNUIThemeConfirmButtonFieldValue("textColor", inheritedAXNUITheme.confirmButton.textColor);
    };


    const handleActionButtonBorderRadiusChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIThemeConfirmButtonFieldValue("cornerRadius", event.target.value);
    };

    const handleResetActionButtonBorderRadius = () => {
        updateAXNUIThemeConfirmButtonFieldValue("cornerRadius", inheritedAXNUITheme.confirmButton.cornerRadius);
    };

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.confirmButton.backgroundColor}
                currentThemingPropertyValue={axnuiTheme.confirmButton.backgroundColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-action-button-background-color-picker"}
                    automationId={"theming-action-button-background-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonBackgroundColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonBackgroundColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.confirmButton.backgroundColor || ""}
                    onChange={handleActionButtonBackgroundColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonBackgroundColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetActionButtonBackgroundColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.confirmButton.textColor}
                currentThemingPropertyValue={axnuiTheme.confirmButton.textColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-action-button-text-color-picker"}
                    automationId={"theming-action-button-text-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonTextColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonTextColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.confirmButton.textColor || ""}
                    onChange={handleActionButtonTextColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonTextColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetActionButtonTextColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.confirmButton.cornerRadius}
                currentThemingPropertyValue={axnuiTheme.confirmButton.cornerRadius}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-action-button-border-radius-input"}
                    automationId={"theming-action-button-border-radius-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonBorderRadius.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonBorderRadius.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleActionButtonBorderRadiusChange}
                    value={axnuiTheme.confirmButton.cornerRadius || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.buttonBorderRadius.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetActionButtonBorderRadius}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.buttonBorderRadiusError}
        </div>
    );
};